/**
 * Exercise Template Service
 * Fetches exercise templates from Hevy API and combines with static templates
 */

import type { HevyExerciseTemplatesResponse, HevyExerciseTemplate } from '../types'

// Simple fetch function to replace fetcher (following workoutService pattern)
const fetcher = async <T>(url: string, options?: RequestInit): Promise<T> => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers
      }
    })

    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`)
      throw error
    }

    return response.json()
  } catch (error) {
    // Re-throw for proper error handling in hooks
    throw error
  }
}

export class ExerciseTemplateService {
  /**
   * Fetch exercise templates from Hevy API with pagination
   */
  static async fetchExerciseTemplates(
    apiKey: string,
    page = 1
  ): Promise<{ templates: HevyExerciseTemplate[]; hasMore: boolean }> {
    try {
      const response = await fetcher<HevyExerciseTemplatesResponse>(
        `https://api.hevyapp.com/v1/exercise_templates?page=${page}`,
        {
          headers: {
            'api-key': apiKey
          }
        }
      )

      return {
        templates: response.exercise_templates || [],
        hasMore: response.page < response.page_count
      }
    } catch (error) {
      // Don't log 404 errors as they're expected when there are no more pages
      if (error instanceof Error && !error.message.includes('404')) {
        console.error('Failed to fetch exercise templates:', error)
      }
      return { templates: [], hasMore: false }
    }
  }

  /**
   * Fetch all exercise templates from Hevy API
   */
  static async fetchAllExerciseTemplates(apiKey: string): Promise<HevyExerciseTemplate[]> {
    try {
      // Fetch first page to get page count
      const firstPage = await this.fetchExerciseTemplates(apiKey, 1)
      const allTemplates = [...firstPage.templates]

      // If there are more pages, fetch them in parallel
      if (firstPage.hasMore) {
        // Get total page count by making a request and checking page_count
        const response = await fetcher<HevyExerciseTemplatesResponse>(
          'https://api.hevyapp.com/v1/exercise_templates?page=1',
          {
            headers: { 'api-key': apiKey }
          }
        )

        const totalPages = response.page_count

        if (totalPages > 1) {
          // Fetch remaining pages in parallel (pages 2 through totalPages)
          const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2)
          
          const pagePromises = remainingPages.map(page => 
            this.fetchExerciseTemplates(apiKey, page).catch(() => ({ templates: [], hasMore: false }))
          )

          const results = await Promise.allSettled(pagePromises)

          // Collect templates from successful fetches
          results.forEach((result) => {
            if (result.status === 'fulfilled' && result.value.templates.length > 0) {
              allTemplates.push(...result.value.templates)
            }
          })
        }
      }

      return allTemplates
    } catch (error) {
      console.error('Failed to fetch all exercise templates:', error)
      return []
    }
  }
}